/**
 * Predefined UI themes.
 *
 * Each theme overrides:
 *  - CSS custom properties used by shadcn/ui components (--background, --primary, etc.)
 *  - Hardcoded Tailwind utility classes (bg-sage-*, text-sage-*, border-sage-*) which
 *    are generated at build time and therefore need runtime !important overrides.
 */

export const THEMES = [
  {
    id: 'teal',
    name: 'Teal Classic',
    description: 'The default deep-teal palette',
    primaryColor: '#0D9488',
    bgColor: '#F8FAFC',
    sage: {
      50: '#F0FDFA',
      100: '#CCFBF1',
      200: '#99F6E4',
      300: '#5EEAD4',
      400: '#2DD4BF',
      500: '#0D9488',
      600: '#0F766E',
      700: '#115E59',
      800: '#134E4A',
      900: '#042F2E',
    },
    cssVars: {
      '--background': '210 40% 98%',
      '--foreground': '215 25% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '215 25% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '215 25% 18%',
      '--primary': '174 80% 32%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '43 96% 50%',
      '--secondary-foreground': '215 25% 18%',
      '--muted': '210 20% 94%',
      '--muted-foreground': '215 15% 46%',
      '--accent': '258 90% 66%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '214 20% 88%',
      '--input': '214 20% 88%',
      '--ring': '174 80% 32%',
    },
  },
  {
    id: 'warm-pet',
    name: 'Warm Pet',
    description: 'Friendly warm-teal with cream background',
    primaryColor: '#2EC4B6',
    bgColor: '#FFF9F2',
    sage: {
      50: '#EDFAF9',
      100: '#D0F5F3',
      200: '#A5EDE9',
      300: '#6ADFD9',
      400: '#3ECFCA',
      500: '#2EC4B6',
      600: '#20A89B',
      700: '#178A7F',
      800: '#136B63',
      900: '#0E504A',
    },
    cssVars: {
      '--background': '38 100% 97%',
      '--foreground': '30 33% 12%',
      '--card': '0 0% 100%',
      '--card-foreground': '30 33% 12%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '30 33% 12%',
      '--primary': '174 62% 47%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '37 100% 55%',
      '--secondary-foreground': '30 33% 12%',
      '--muted': '38 40% 94%',
      '--muted-foreground': '22 15% 52%',
      '--accent': '30 33% 36%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '38 30% 88%',
      '--input': '38 30% 88%',
      '--ring': '174 62% 47%',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean Blue',
    description: 'Cool sky-blue coastal tones',
    primaryColor: '#0284C7',
    bgColor: '#F0F9FF',
    sage: {
      50: '#F0F9FF',
      100: '#E0F2FE',
      200: '#BAE6FD',
      300: '#7DD3FC',
      400: '#38BDF8',
      500: '#0284C7',
      600: '#0369A1',
      700: '#075985',
      800: '#0C4A6E',
      900: '#082F49',
    },
    cssVars: {
      '--background': '204 100% 97%',
      '--foreground': '215 25% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '215 25% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '215 25% 18%',
      '--primary': '200 98% 39%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '43 96% 50%',
      '--secondary-foreground': '215 25% 18%',
      '--muted': '204 40% 94%',
      '--muted-foreground': '215 15% 46%',
      '--accent': '258 90% 66%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '204 20% 88%',
      '--input': '204 20% 88%',
      '--ring': '200 98% 39%',
    },
  },
  {
    id: 'forest',
    name: 'Forest Green',
    description: 'Fresh natural green palette',
    primaryColor: '#16A34A',
    bgColor: '#F0FDF4',
    sage: {
      50: '#F0FDF4',
      100: '#DCFCE7',
      200: '#BBF7D0',
      300: '#86EFAC',
      400: '#4ADE80',
      500: '#16A34A',
      600: '#15803D',
      700: '#166534',
      800: '#14532D',
      900: '#052E16',
    },
    cssVars: {
      '--background': '138 76% 97%',
      '--foreground': '215 25% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '215 25% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '215 25% 18%',
      '--primary': '142 76% 36%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '43 96% 50%',
      '--secondary-foreground': '215 25% 18%',
      '--muted': '138 40% 94%',
      '--muted-foreground': '215 15% 46%',
      '--accent': '258 90% 66%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '138 20% 88%',
      '--input': '138 20% 88%',
      '--ring': '142 76% 36%',
    },
  },
  {
    id: 'lavender',
    name: 'Lavender',
    description: 'Soft purple for a calming feel',
    primaryColor: '#7C3AED',
    bgColor: '#FAF5FF',
    sage: {
      50: '#FAF5FF',
      100: '#EDE9FE',
      200: '#DDD6FE',
      300: '#C4B5FD',
      400: '#A78BFA',
      500: '#7C3AED',
      600: '#6D28D9',
      700: '#5B21B6',
      800: '#4C1D95',
      900: '#2E1065',
    },
    cssVars: {
      '--background': '270 100% 98%',
      '--foreground': '215 25% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '215 25% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '215 25% 18%',
      '--primary': '263 70% 50%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '43 96% 50%',
      '--secondary-foreground': '215 25% 18%',
      '--muted': '270 40% 95%',
      '--muted-foreground': '215 15% 46%',
      '--accent': '43 96% 50%',
      '--accent-foreground': '215 25% 18%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '270 20% 88%',
      '--input': '270 20% 88%',
      '--ring': '263 70% 50%',
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    description: 'Warm rose-pink tones',
    primaryColor: '#E11D48',
    bgColor: '#FFF1F2',
    sage: {
      50: '#FFF1F2',
      100: '#FFE4E6',
      200: '#FECDD3',
      300: '#FDA4AF',
      400: '#FB7185',
      500: '#E11D48',
      600: '#BE123C',
      700: '#9F1239',
      800: '#881337',
      900: '#4C0519',
    },
    cssVars: {
      '--background': '351 100% 98%',
      '--foreground': '215 25% 18%',
      '--card': '0 0% 100%',
      '--card-foreground': '215 25% 18%',
      '--popover': '0 0% 100%',
      '--popover-foreground': '215 25% 18%',
      '--primary': '347 77% 50%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '43 96% 50%',
      '--secondary-foreground': '215 25% 18%',
      '--muted': '351 40% 95%',
      '--muted-foreground': '215 15% 46%',
      '--accent': '258 90% 66%',
      '--accent-foreground': '0 0% 100%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '351 20% 88%',
      '--input': '351 20% 88%',
      '--ring': '347 77% 50%',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Dark moody slate for night use',
    primaryColor: '#818CF8',
    bgColor: '#0F172A',
    sage: {
      50: '#EEF2FF',
      100: '#E0E7FF',
      200: '#C7D2FE',
      300: '#A5B4FC',
      400: '#818CF8',
      500: '#818CF8',
      600: '#6366F1',
      700: '#4F46E5',
      800: '#4338CA',
      900: '#3730A3',
    },
    cssVars: {
      '--background': '222 47% 11%',
      '--foreground': '210 40% 96%',
      '--card': '217 33% 17%',
      '--card-foreground': '210 40% 96%',
      '--popover': '217 33% 17%',
      '--popover-foreground': '210 40% 96%',
      '--primary': '239 84% 67%',
      '--primary-foreground': '0 0% 100%',
      '--secondary': '217 33% 25%',
      '--secondary-foreground': '210 40% 96%',
      '--muted': '217 33% 22%',
      '--muted-foreground': '215 20% 65%',
      '--accent': '43 96% 50%',
      '--accent-foreground': '215 25% 18%',
      '--destructive': '0 84% 60%',
      '--destructive-foreground': '0 0% 100%',
      '--border': '217 33% 25%',
      '--input': '217 33% 25%',
      '--ring': '239 84% 67%',
    },
  },
];

export const DEFAULT_THEME_ID = 'teal';
